import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCalendar, FiCheck, FiUser, FiStar, FiAlertCircle, FiSun, FiCloud, FiCloudRain, FiCloudSnow, FiChevronDown, FiChevronUp, FiThermometer, FiWind, FiDroplet, FiUsers, FiSunrise, FiSunset, FiCheckCircle, FiHelpCircle, FiExternalLink, FiClock } from 'react-icons/fi';
import { IoFlameOutline } from 'react-icons/io5';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../contexts/AuthContext';
import { getSunTimes } from '../services/sunService';
import { formatGolfNowPrice } from '../services/golfNowService';
import { formatRoundDate } from '../utils/timeHelpers';

// Smart Result Card Component
const SmartResultCard = ({ result, expanded, onToggle, onCreateRound, isCreatingRound = false, isSolo = false }) => {
  const { user } = useAuth();

  // Check if result has GolfNow data
  const hasGolfNow = !!(result.golfnow && result.golfnow.exactTime);

  // GolfNow booking URL - the rateId in the URL already determines the player count
  // so no additional parameters needed
  const golfnowBookingUrl = result.golfnow?.bookingUrl || null;

  // Helper functions for member display
  const getMemberDisplayName = (member, currentUserId) => {
    if (member.userId === currentUserId) {
      return 'You';
    }
    const name = member.name || member;
    return typeof name === 'string' ? name : 'Member';
  };

  const getAvailableMemberDisplayName = (member, currentUserId) => {
    // Enhanced safety check for member object
    if (!member) {
      return 'Unknown Member';
    }

    // Handle case where member is just a userId string (fallback)
    if (typeof member === 'string') {
      return member === currentUserId ? 'You' : 'Member';
    }

    // Handle case where member is not an object (could be a service response)
    if (typeof member !== 'object') {
      return 'Member';
    }

    try {
      // Check if this is the current user
      if (member.userInfo?.id === currentUserId || member.userId === currentUserId) {
        return 'You';
      }

      // Try to get first name from user metadata (skip generic "User" fallback)
      const firstName = member.userInfo?.user_metadata?.first_name;
      if (firstName && typeof firstName === 'string' && firstName.trim() && firstName.trim() !== 'User') {
        return firstName.trim();
      }

      // Try to get name and extract first name (skip generic "User XXXX" fallback)
      const fullName = member.name || member.userInfo?.user_metadata?.full_name;
      if (fullName && typeof fullName === 'string' && fullName.trim() && !fullName.trim().startsWith('User ')) {
        return fullName.trim().split(' ')[0]; // Get first name only
      }

      // Try member_name directly if available (from group_members table)
      const memberName = member.userInfo?.member_name || member.member_name;
      if (memberName && typeof memberName === 'string' && memberName.trim()) {
        return memberName.trim().split(' ')[0]; // Get first name only
      }

      // Try to get email and extract name (skip generic user-xxxx fallbacks)
      const email = member.userInfo?.email;
      if (email && typeof email === 'string' && email.includes('@')) {
        const emailName = email.split('@')[0];
        // Skip if it looks like a fallback (user-xxxx pattern)
        if (emailName && typeof emailName === 'string' && emailName.trim() && !emailName.startsWith('user-')) {
          return emailName.trim();
        }
      }

    } catch (error) {
      console.warn('Error processing member display name:', error);
    }

    // Final fallback - show "Golfer" instead of generic "Member"
    return 'Golfer';
  };

  // Weather and scoring helper functions
  const getWeatherIcon = (weather) => {
    if (!weather) return FiCloud;
    // Safely extract weather description, ensuring it's always a string
    let condition = '';
    try {
      if (weather.weather && Array.isArray(weather.weather) && weather.weather[0]) {
        const desc = weather.weather[0].description;
        if (typeof desc === 'string') {
          condition = desc.toLowerCase();
        }
      }
    } catch (error) {
      console.warn('Error extracting weather description:', error);
    }
    
    if (condition.includes('clear')) return FiSun;
    if (condition.includes('cloud')) return FiCloud;
    if (condition.includes('rain') || condition.includes('drizzle')) return FiCloudRain;
    if (condition.includes('snow')) return FiCloudSnow;
    return FiCloud;
  };

  // Weather metric scoring functions using the same thresholds as the main weather service
  const getWeatherMetricCategory = (value, type) => {
    if (value === null || value === undefined) return 'unknown';

    switch (type) {
      case 'temperature':
        if (value >= 18 && value <= 24) return 'excellent';
        if (value >= 15 && value <= 27) return 'very-good';
        if (value >= 13 && value <= 29) return 'good';
        if (value >= 7 && value <= 32) return 'okay';
        if (value >= 2 && value <= 35) return 'poor';
        return 'severe';

      case 'windSpeed':
        if (value <= 5) return 'excellent';
        if (value <= 10) return 'very-good';
        if (value <= 15) return 'good';
        if (value <= 22) return 'okay';
        if (value <= 30) return 'poor';
        return 'severe';

      case 'precipitation':
        if (value <= 0.5) return 'excellent';
        if (value <= 2) return 'very-good';
        if (value <= 5) return 'good';
        if (value <= 10) return 'okay';
        if (value <= 20) return 'poor';
        return 'severe';

      case 'humidity':
        if (value >= 45 && value <= 65) return 'excellent';
        if (value >= 40 && value <= 70) return 'very-good';
        if (value >= 35 && value <= 75) return 'good';
        if (value >= 25 && value <= 85) return 'okay';
        if (value >= 15 && value <= 92) return 'poor';
        return 'severe';

      default:
        return 'unknown';
    }
  };

  // Ground quality helper functions
  const getGroundQualityIcon = (category) => {
    switch(category) {
      case 'firm': return FiCheckCircle;      // Green checkmark
      case 'soft': return FiDroplet;          // Blue droplet
      case 'wet': return FiCloudRain;         // Yellow/orange rain cloud
      case 'waterlogged': return FiAlertCircle; // Red alert
      default: return FiHelpCircle;           // Grey question mark
    }
  };

  const getGroundQualityColor = (category) => {
    switch(category) {
      case 'firm': return 'border-green-800 bg-green-50 text-green-800';
      case 'soft': return 'border-amber-600 bg-amber-50 text-amber-600';
      case 'wet': return 'border-orange-700 bg-orange-50 text-orange-700';
      case 'waterlogged': return 'border-red-700 bg-red-50 text-red-700';
      default: return 'border-amber-600 bg-amber-50 text-amber-600';
    }
  };

  const getGroundQualityIconColor = (category) => {
    switch(category) {
      case 'firm': return 'text-green-800';
      case 'soft': return 'text-amber-600';
      case 'wet': return 'text-orange-700';
      case 'waterlogged': return 'text-red-700';
      default: return 'text-amber-600';
    }
  };

  const getGroundQualityTextColor = (category) => {
    switch(category) {
      case 'firm': return 'text-green-800';
      case 'soft': return 'text-amber-600';
      case 'wet': return 'text-orange-700';
      case 'waterlogged': return 'text-red-700';
      default: return 'text-amber-600';
    }
  };

  const formatGroundQualityCategory = (category) => {
    if (!category) return 'Unknown';
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  // Generate contextual description for ground quality card
  const getGroundContextDescription = (groundQuality) => {
    const tempMod = groundQuality.temperatureModifier;
    const category = groundQuality.category;

    // Frozen ground - special case
    if (tempMod === 'frozen') {
      return 'Ground frozen';
    }

    // Get rainfall data
    const rainfall = groundQuality.effectiveRainfall ?? groundQuality.weightedRainfall ?? 0;
    const days = groundQuality.historyDays || 7;

    // Format rainfall amount
    const rainfallStr = rainfall < 1 ? '<1mm' : `${Math.round(rainfall)}mm`;

    // Context varies by category and evaporation balance
    if (category === 'firm') {
      if (tempMod === 'drying') {
        return `${rainfallStr} effective rain, drying fast`;
      }
      return `Only ${rainfallStr} in last ${days} days`;
    }

    // Soft/wet/waterlogged - explain the rainfall
    if (tempMod === 'damp') {
      return `${rainfallStr} in last ${days} days, slow to dry`;
    }

    return `${rainfallStr} in last ${days} days`;
  };

  // 6-tier category colour mapping for weather metrics (red-to-green gradient, amber for neutral)
  const getWeatherMetricColor = (category) => {
    switch (category) {
      case 'excellent': return 'border-green-800 bg-green-50 text-green-800';
      case 'very-good': return 'border-lime-800 bg-lime-50 text-lime-800';
      case 'ideal': return 'border-green-800 bg-green-50 text-green-800'; // Legacy support
      case 'good': return 'border-yellow-700 bg-yellow-50 text-yellow-700';
      case 'okay': return 'border-amber-600 bg-amber-50 text-amber-600';
      case 'poor': return 'border-orange-700 bg-orange-50 text-orange-700';
      case 'severe': return 'border-red-700 bg-red-50 text-red-700';
      default: return 'border-amber-600 bg-amber-50 text-amber-600';
    }
  };

  // Helper for getting icon/text colours by category (red-to-green gradient, amber for neutral)
  const getCategoryIconColor = (category) => {
    switch (category) {
      case 'excellent':
      case 'ideal': return 'text-green-800';
      case 'very-good': return 'text-lime-800';
      case 'good': return 'text-yellow-700';
      case 'okay': return 'text-amber-600';
      case 'poor': return 'text-orange-700';
      case 'severe': return 'text-red-700';
      default: return 'text-amber-600';
    }
  };

  const getCategoryTextColor = (category) => {
    switch (category) {
      case 'excellent':
      case 'ideal': return 'text-green-800';
      case 'very-good': return 'text-lime-800';
      case 'good': return 'text-yellow-700';
      case 'okay': return 'text-amber-600';
      case 'poor': return 'text-orange-700';
      case 'severe': return 'text-red-700';
      default: return 'text-amber-600';
    }
  };

  // Get weather score icon based on 6-tier category and contributing factors
  const getWeatherScoreIcon = (weather, category) => {
    if (!weather || !category) return FiCloud;

    switch (category) {
      case 'excellent':
      case 'ideal': // Legacy support
        return FiSun;

      case 'very-good':
      case 'good':
        return FiCloud;

      case 'okay':
      case 'poor':
      case 'severe':
        // Determine which factor is contributing most negatively
        const temp = weather.temperature;
        const windSpeed = weather.windSpeed;
        const precipitation = weather.precipitation;
        const humidity = weather.humidity;
        
        // Calculate how far each metric is from ideal conditions
        const tempDistance = temp ? Math.min(
          Math.abs(temp - 18), // Distance from ideal lower bound
          Math.abs(temp - 27)   // Distance from ideal upper bound
        ) : 0;
        
        const windDistance = windSpeed ? Math.max(0, windSpeed - 10) : 0; // How much over ideal 10mph
        const precipDistance = precipitation ? Math.max(0, precipitation - 2) : 0; // How much over ideal 2mm
        
        const humidityDistance = humidity ? Math.min(
          Math.abs(humidity - 40), // Distance from ideal lower bound
          Math.abs(humidity - 70)   // Distance from ideal upper bound
        ) : 0;
        
        // Find the worst offender (normalize to compare different scales)
        const normalizedTempDistance = tempDistance / 15; // Normalize by typical temperature range
        const normalizedWindDistance = windDistance / 15; // Normalize by typical wind range
        const normalizedPrecipDistance = precipDistance / 10; // Normalize by typical precipitation range
        const normalizedHumidityDistance = humidityDistance / 30; // Normalize by typical humidity range
        
        // Return icon for the worst performing metric
        const maxDistance = Math.max(
          normalizedTempDistance,
          normalizedWindDistance, 
          normalizedPrecipDistance,
          normalizedHumidityDistance
        );
        
        if (maxDistance === normalizedPrecipDistance && precipitation > 2) {
          return FiCloudRain;
        } else if (maxDistance === normalizedWindDistance && windSpeed > 10) {
          return FiWind;
        } else if (maxDistance === normalizedTempDistance) {
          return FiThermometer;
        } else if (maxDistance === normalizedHumidityDistance) {
          return FiDroplet;
        }
        
        // Default fallback for fair/poor
        return FiCloudRain;
      
      default:
        return FiCloud;
    }
  };

  // Format weather category for display (6-tier system)
  const formatWeatherCategory = (category, short = false) => {
    if (!category) return 'N/A';
    // Handle hyphenated categories like 'very-good'
    const formatted = category.split('-').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
    return short ? formatted : formatted + ' Weather';
  };

  // 6-tier weather category colour mapping (red-to-green gradient, amber for neutral)
  const getWeatherColor = (category) => {
    switch (category) {
      case 'excellent': return 'border-green-800 bg-green-50 text-green-800';
      case 'very-good': return 'border-lime-800 bg-lime-50 text-lime-800';
      case 'ideal': return 'border-green-800 bg-green-50 text-green-800'; // Legacy support
      case 'good': return 'border-yellow-700 bg-yellow-50 text-yellow-700';
      case 'okay': return 'border-amber-600 bg-amber-50 text-amber-600';
      case 'poor': return 'border-orange-700 bg-orange-50 text-orange-700';
      case 'severe': return 'border-red-700 bg-red-50 text-red-700';
      default: return 'border-amber-600 bg-amber-50 text-amber-600';
    }
  };

  const getAvailabilityColour = (available, total) => {
    const percentage = (available / total) * 100;
    if (percentage >= 80) return 'border-green-800 bg-green-50 text-green-800';
    if (percentage >= 60) return 'border-amber-600 bg-amber-50 text-amber-600';
    if (percentage >= 40) return 'border-orange-700 bg-orange-50 text-orange-700';
    return 'border-red-700 bg-red-50 text-red-700';
  };

  // 6-tier score badge colour based on composite score (includes background, border, and text)
  const getScoreBadgeColour = (score) => {
    if (score >= 9.0) return 'border-green-800 bg-green-50 text-green-800';   // Excellent
    if (score >= 8.0) return 'border-lime-700 bg-lime-50 text-lime-800';      // Very Good
    if (score >= 7.0) return 'border-yellow-600 bg-yellow-50 text-yellow-700'; // Good
    if (score >= 5.0) return 'border-amber-500 bg-amber-50 text-amber-600';   // Okay
    if (score >= 3.0) return 'border-orange-600 bg-orange-50 text-orange-700'; // Poor
    return 'border-red-700 bg-red-50 text-red-700';                           // Severe
  };

  const scoreBadgeColour = getScoreBadgeColour(result.compositeScore || 0);
  const badgeClass = 'inline-flex items-center px-2 py-1.5 rounded-lg text-xs font-medium border justify-center hover:shadow-sm transition-all duration-200 cursor-default whitespace-nowrap h-7 min-w-[3.5rem] px-3';
  const hasWeatherData = result.weather && !result.weatherError;
  const showWeatherBadge = hasWeatherData || result.golfCourse;
  const showPriceBadge = hasGolfNow && result.golfnow?.price > 0;
  const showMembersBadge = !isSolo;

  // Calculate sun times for the result's date and course location
  const sunTimes = useMemo(() => {
    // First check if sun times are already in weather.golfScore (calculated by weatherService)
    if (result.weather?.golfScore?.sunTimes) {
      return result.weather.golfScore.sunTimes;
    }

    // Otherwise calculate locally if we have coordinates
    const coords = result.golfCourse?.coordinates;
    if (!coords?.lat || !coords?.lon || !result.date) {
      return null;
    }

    try {
      const times = getSunTimes(result.date, coords.lat, coords.lon);
      return times ? { sunrise: times.sunrise, sunset: times.sunset } : null;
    } catch (error) {
      console.warn('Error calculating sun times:', error);
      return null;
    }
  }, [result.date, result.golfCourse?.coordinates, result.weather?.golfScore?.sunTimes]);

  // Get daylight category from composite score calculation or calculate locally
  const getDaylightCategory = () => {
    // Use pre-calculated category from composite score (set by calculateCompositeScore)
    // This is the most accurate as it uses the same logic that affects the score
    if (result.daylightCategory && result.daylightCategory !== 'unknown') {
      return result.daylightCategory;
    }

    // Fallback: Use category from weather service if available
    if (result.weather?.golfScore?.daylightCategory) {
      return result.weather.golfScore.daylightCategory;
    }

    // Final fallback: calculate locally based on tee time vs sun times
    if (!sunTimes || !result.window?.start) return 'unknown';

    try {
      const [teeHour, teeMin] = result.window.start.split(':').map(Number);
      const [sunriseHour, sunriseMin] = sunTimes.sunrise.split(':').map(Number);
      const [sunsetHour, sunsetMin] = sunTimes.sunset.split(':').map(Number);

      const teeMinutes = teeHour * 60 + teeMin;
      const sunriseMinutes = sunriseHour * 60 + sunriseMin;
      const sunsetMinutes = sunsetHour * 60 + sunsetMin;

      // Assume 4-hour round for scoring
      const endMinutes = teeMinutes + 240;

      const marginAfterSunrise = teeMinutes - sunriseMinutes;
      const marginBeforeSunset = sunsetMinutes - endMinutes;

      if (marginAfterSunrise < -30 || marginBeforeSunset < -30) return 'severe';
      if (marginAfterSunrise < 0 || marginBeforeSunset < 0) return 'poor';
      if (marginAfterSunrise < 15 || marginBeforeSunset < 15) return 'okay';
      if (marginAfterSunrise < 30 || marginBeforeSunset < 30) return 'good';
      if (marginAfterSunrise < 45 || marginBeforeSunset < 45) return 'very-good';
      return 'excellent';
    } catch {
      return 'unknown';
    }
  };

  const daylightCategory = getDaylightCategory();

  const toggleExpanded = (e) => {
    e.stopPropagation();
    onToggle();
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="backdrop-blur-sm bg-surface rounded-xl shadow-card border border-border-subtle overflow-hidden hover:shadow-card-hover transition-all duration-300 group"
    >
      {/* Compact Header */}
      <div
        className="p-4 cursor-pointer"
        onClick={toggleExpanded}
      >
        {/* Header Row - Date/Time + Expand (mobile), Date/Time + Badges + Expand (desktop) */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-shrink-0">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-accent/10 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiCalendar} className="w-5 h-5 md:w-6 md:h-6 text-accent" />
            </div>
            <div>
              <h4 className="font-bold text-text-primary text-base md:text-lg leading-tight">
                <span className="hidden sm:inline">
                  {formatRoundDate(
                    {
                      date: result.date,
                      course_latitude: result.golfCourse?.coordinates?.lat,
                      course_longitude: result.golfCourse?.coordinates?.lon,
                    },
                    { weekday: 'long', day: 'numeric', month: 'long', year: undefined },
                  )}
                </span>
                <span className="sm:hidden">
                  {formatRoundDate(
                    {
                      date: result.date,
                      course_latitude: result.golfCourse?.coordinates?.lat,
                      course_longitude: result.golfCourse?.coordinates?.lon,
                    },
                    { weekday: 'short', day: 'numeric', month: 'short', year: undefined },
                  )}
                </span>
              </h4>
              <p className="text-text-secondary font-medium text-sm">
                {result.window ? `${result.window.start} - ${result.window.end}` : 'Time TBD'}
              </p>
            </div>
          </div>

          {/* Badges - flex inline on desktop */}
          <div className="hidden md:flex items-center gap-2 ml-auto mr-3">
              {/* Score badge - use score-based colour for solo/GolfNow, availability-based for groups with members */}
              <span className={`${badgeClass} ${scoreBadgeColour}`}>
                <SafeIcon icon={FiStar} className="w-3 h-3 mr-1" />
                {typeof result.compositeScore === 'number' ? result.compositeScore.toFixed(1) : 'N/A'}
              </span>

              {/* Weather badge */}
              {hasWeatherData && (() => {
                const weatherCategory = result.weather.golfScore?.category;
                const categoryText = typeof weatherCategory === 'string' ? formatWeatherCategory(weatherCategory, false) : 'N/A';
                return (
                  <span className={`${badgeClass} ${getWeatherColor(weatherCategory)}`}>
                    <SafeIcon icon={getWeatherScoreIcon(result.weather, weatherCategory)} className="w-3 h-3 mr-1 flex-shrink-0" />
                    {categoryText}
                  </span>
                );
              })()}

              {/* GolfNow price badge */}
              {showPriceBadge && (
                <span className={`${badgeClass} ${result.golfnow.isHotDeal ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-accent/30 bg-accent/10 text-accent'}`}>
                  {result.golfnow.isHotDeal && <IoFlameOutline className="w-3.5 h-3.5 mr-1" />}
                  {formatGolfNowPrice(result.golfnow.price, result.golfnow.currency)}
                </span>
              )}

              {/* Members count - hidden for solo rounds */}
              {showMembersBadge && (
                <span className={`${badgeClass} bg-green-50 text-green-700 border-green-200 hover:bg-green-100`}>
                  <SafeIcon icon={FiUsers} className="w-3 h-3 mr-1" />
                  {result.availableMembers?.length || 0}/{result.totalMembers || 0}
                </span>
              )}
          </div>

          <button
            onClick={toggleExpanded}
            className="w-8 h-8 md:w-10 md:h-10 bg-surface rounded-lg flex items-center justify-center hover:bg-card transition-all duration-200 group-hover:bg-card flex-shrink-0"
            aria-label={expanded ? "Collapse details" : "Expand details"}
          >
            <SafeIcon
              icon={expanded ? FiChevronUp : FiChevronDown}
              className="w-4 h-4 md:w-5 md:h-5 text-text-secondary"
            />
          </button>
        </div>

        {/* Mobile badges - below header */}
        <div className="flex items-center gap-1.5 mt-3 md:hidden">
              <span className={`${badgeClass} ${scoreBadgeColour}`}>
                <SafeIcon icon={FiStar} className="w-3 h-3 mr-1" />
                {typeof result.compositeScore === 'number' ? result.compositeScore.toFixed(1) : 'N/A'}
              </span>
              {(result.weather && !result.weatherError) && (() => {
                const weatherCategory = result.weather.golfScore?.category;
                const categoryText = typeof weatherCategory === 'string' ? formatWeatherCategory(weatherCategory, true) : 'N/A';
                return (
                  <span className={`${badgeClass} ${getWeatherColor(weatherCategory)}`}>
                    <SafeIcon icon={getWeatherScoreIcon(result.weather, weatherCategory)} className="w-3 h-3 mr-1 flex-shrink-0" />
                    {categoryText}
                  </span>
                );
              })()}
              {hasGolfNow && result.golfnow.price > 0 && (
                <span className={`${badgeClass} ${result.golfnow.isHotDeal ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-accent/30 bg-accent/10 text-accent'}`}>
                  {result.golfnow.isHotDeal && <IoFlameOutline className="w-3.5 h-3.5 mr-1" />}
                  {formatGolfNowPrice(result.golfnow.price, result.golfnow.currency)}
                </span>
              )}
              {!isSolo && (
                <span className={`${badgeClass} bg-green-50 text-green-700 border-green-200`}>
                  <SafeIcon icon={FiUsers} className="w-3 h-3 mr-1" />
                  {result.availableMembers?.length || 0}/{result.totalMembers || 0}
                </span>
              )}
        </div>
      </div>

      {/* Expanded Content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="border-t border-border-subtle"
          >
            <div className="p-4">
              <div className="md:flex">
              {/* Weather Details Section */}
              <div className="mb-6 md:mb-0 md:w-1/2 md:pr-6">
                {/* Weather Details */}
                {(result.weather && !result.weatherError) || sunTimes ? (
                  <div>
                  <label className="text-sm font-semibold text-text-secondary mb-3 block">Weather Details</label>
                  {/* 2 cols on mobile, 3 cols on desktop (2 rows of 3) */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {/* Temperature */}
                    {result.weather && (() => {
                      const temp = result.weather?.temperature;
                      const tempCategory = getWeatherMetricCategory(temp, 'temperature');
                      const tempColorClass = getWeatherMetricColor(tempCategory);
                      const iconColor = getCategoryIconColor(tempCategory);
                      const textColor = getCategoryTextColor(tempCategory);
                      const labelColor = getCategoryIconColor(tempCategory);
                      
                      return (
                        <div className={`${tempColorClass} border px-4 py-3 rounded-xl text-center hover:shadow-sm transition-all duration-200`}>
                          <SafeIcon icon={FiThermometer} className={`w-5 h-5 ${iconColor} mx-auto mb-2`} />
                          <p className={`text-sm font-semibold ${textColor}`}>
                            {(() => {
                              try {
                                return typeof temp === 'number' ? `${Math.round(temp)}°C` : 'N/A';
                              } catch (error) {
                                console.warn('Error displaying temperature:', error);
                                return 'N/A';
                              }
                            })()}
                          </p>
                          <p className={`text-xs ${labelColor} mt-1`}>Temperature</p>
                        </div>
                      );
                    })()}
                    
                    {/* Wind Speed */}
                    {result.weather && (() => {
                      const windSpeed = result.weather?.windSpeed;
                      const windCategory = getWeatherMetricCategory(windSpeed, 'windSpeed');
                      const windColorClass = getWeatherMetricColor(windCategory);
                      const iconColor = getCategoryIconColor(windCategory);
                      const textColor = getCategoryTextColor(windCategory);
                      const labelColor = getCategoryIconColor(windCategory);
                      
                      return (
                        <div className={`${windColorClass} border px-4 py-3 rounded-xl text-center hover:shadow-sm transition-all duration-200`}>
                          <SafeIcon icon={FiWind} className={`w-5 h-5 ${iconColor} mx-auto mb-2`} />
                          <p className={`text-sm font-semibold ${textColor}`}>
                            {(() => {
                              try {
                                return typeof windSpeed === 'number' ? `${Math.round(windSpeed)} mph` : 'N/A';
                              } catch (error) {
                                console.warn('Error displaying wind speed:', error);
                                return 'N/A';
                              }
                            })()}
                          </p>
                          <p className={`text-xs ${labelColor} mt-1`}>Wind Speed</p>
                        </div>
                      );
                    })()}
                    
                    {/* Precipitation */}
                    {result.weather && (() => {
                      const precip = result.weather?.precipitation;
                      const precipCategory = getWeatherMetricCategory(precip, 'precipitation');
                      const precipColorClass = getWeatherMetricColor(precipCategory);
                      const iconColor = getCategoryIconColor(precipCategory);
                      const textColor = getCategoryTextColor(precipCategory);
                      const labelColor = getCategoryIconColor(precipCategory);
                      
                      return (
                        <div className={`${precipColorClass} border px-4 py-3 rounded-xl text-center hover:shadow-sm transition-all duration-200`}>
                          <SafeIcon icon={FiCloudRain} className={`w-5 h-5 ${iconColor} mx-auto mb-2`} />
                          <p className={`text-sm font-semibold ${textColor}`}>
                            {(() => {
                              try {
                                return typeof precip === 'number' ? `${Math.round(precip)} mm` : 'N/A';
                              } catch (error) {
                                console.warn('Error displaying precipitation:', error);
                                return 'N/A';
                              }
                            })()}
                          </p>
                          <p className={`text-xs ${labelColor} mt-1`}>{result.weather?.precipitationLabel || 'Rain'}</p>
                        </div>
                      );
                    })()}

                    {/* Humidity */}
                    {result.weather && (() => {
                      const humidity = result.weather?.humidity;
                      const humidityCategory = getWeatherMetricCategory(humidity, 'humidity');
                      const humidityColorClass = getWeatherMetricColor(humidityCategory);
                      const iconColor = getCategoryIconColor(humidityCategory);
                      const textColor = getCategoryTextColor(humidityCategory);
                      const labelColor = getCategoryIconColor(humidityCategory);

                      return (
                        <div className={`${humidityColorClass} border px-4 py-3 rounded-xl text-center hover:shadow-sm transition-all duration-200`}>
                          <SafeIcon icon={FiDroplet} className={`w-5 h-5 ${iconColor} mx-auto mb-2`} />
                          <p className={`text-sm font-semibold ${textColor}`}>
                            {(() => {
                              try {
                                return typeof humidity === 'number' ? `${Math.round(humidity)}%` : 'N/A';
                              } catch (error) {
                                console.warn('Error displaying humidity:', error);
                                return 'N/A';
                              }
                            })()}
                          </p>
                          <p className={`text-xs ${labelColor} mt-1`}>Humidity</p>
                        </div>
                      );
                    })()}

                    {/* Ground Quality */}
                    {result.weather?.groundQuality && (() => {
                      const groundQuality = result.weather.groundQuality;
                      const groundCategory = groundQuality.category;
                      const groundColorClass = getGroundQualityColor(groundCategory);
                      const iconColor = getGroundQualityIconColor(groundCategory);
                      const textColor = getGroundQualityTextColor(groundCategory);
                      const labelColor = getGroundQualityIconColor(groundCategory);

                      // Build tooltip text with v2.0 ground quality fields
                      const tooltipText = (() => {
                        const rainfall = groundQuality.effectiveRainfall ?? groundQuality.weightedRainfall;
                        const parts = [`${rainfall}mm effective rainfall over ${groundQuality.historyDays || 7} days`];

                        // Add softness percentage if available
                        if (groundQuality.softnessIndex !== null && groundQuality.softnessIndex !== undefined) {
                          parts.push(`(softness: ${Math.round(groundQuality.softnessIndex * 100)}%)`);
                        }

                        // Add temperature modifier if not normal
                        if (groundQuality.temperatureModifier && groundQuality.temperatureModifier !== 'normal') {
                          const modifierLabels = {
                            'frozen': '- frozen ground',
                            'drying': '- evap > rainfall (drying)',
                            'damp': '- rainfall > evap (slow to dry)'
                          };
                          parts.push(modifierLabels[groundQuality.temperatureModifier] || '');
                        }

                        // Add limited data indicator
                        if (groundQuality.isEstimate) {
                          parts.push('(limited data)');
                        }

                        return parts.filter(Boolean).join(' ');
                      })();

                      return (
                        <div
                          className={`${groundColorClass} border px-4 py-3 rounded-xl text-center hover:shadow-sm transition-all duration-200 cursor-help`}
                          title={tooltipText}
                        >
                          <SafeIcon icon={getGroundQualityIcon(groundCategory)} className={`w-5 h-5 ${iconColor} mx-auto mb-2`} />
                          <p className={`text-sm font-semibold ${textColor}`}>
                            {formatGroundQualityCategory(groundCategory)}
                          </p>
                          <p className={`text-xs ${labelColor} mt-1`}>Ground</p>
                        </div>
                      );
                    })()}

                    {/* Daylight (Sunrise / Sunset combined) */}
                    {sunTimes && (() => {
                      const daylightColorClass = getWeatherMetricColor(daylightCategory);
                      const iconColor = getCategoryIconColor(daylightCategory);
                      const textColor = getCategoryTextColor(daylightCategory);
                      const labelColor = getCategoryIconColor(daylightCategory);

                      return (
                        <div className={`${daylightColorClass} border px-4 py-3 rounded-xl text-center hover:shadow-sm transition-all duration-200`}>
                          <div className="flex items-center justify-center gap-1 mb-2">
                            <SafeIcon icon={FiSunrise} className={`w-4 h-4 ${iconColor}`} />
                            <span className="text-text-muted">/</span>
                            <SafeIcon icon={FiSunset} className={`w-4 h-4 ${iconColor}`} />
                          </div>
                          <p className={`text-sm font-semibold ${textColor}`}>{sunTimes.sunrise} - {sunTimes.sunset}</p>
                          <p className={`text-xs ${labelColor} mt-1`}>Daylight</p>
                        </div>
                      );
                    })()}
                  </div>
                  </div>
                ) : null}
              </div>

              {/* Available Members - hidden for solo rounds */}
                {!isSolo && (
                  <div className="mb-6 md:w-1/2 md:pl-6 md:border-l md:border-border-subtle">
                  <label className="text-sm font-semibold text-text-secondary mb-3 block">Available Members ({result.availableMembers?.length || 0}/{result.totalMembers || 0})</label>
                  <div className="flex flex-wrap gap-2">
                    {result.availableMembers && Array.isArray(result.availableMembers) && result.availableMembers.slice(0, 5).map((member, index) => {
                      // Enhanced safety check for member data
                      if (!member || typeof member !== 'object') return null;

                      // Generate a safe key
                      const safeKey = member.userId || member.userInfo?.id || `member-${index}`;

                      // Safely get display name with multiple fallbacks
                      let displayName = 'Member';
                      try {
                        const nameResult = getAvailableMemberDisplayName(member, user?.id);
                        if (typeof nameResult === 'string' && nameResult.trim()) {
                          displayName = nameResult.trim();
                        } else if (nameResult && typeof nameResult === 'object') {
                          // Handle case where service response object was returned - never render objects
                          if (nameResult.data && typeof nameResult.data === 'string') {
                            displayName = nameResult.data;
                          } else if (nameResult.name && typeof nameResult.name === 'string') {
                            displayName = nameResult.name;
                          } else {
                            // If it's any other object structure, fallback to safe string
                            displayName = 'Member';
                          }
                        } else {
                          // Ensure we never render non-string types
                          displayName = 'Member';
                        }
                      } catch (error) {
                        console.warn('Error getting member display name:', error);
                        displayName = 'Member';
                      }

                      return (
                        <span
                          key={safeKey}
                          className="inline-flex items-center bg-green-100 text-green-800 px-3 py-1 rounded-xl text-sm font-medium"
                        >
                          <SafeIcon icon={FiUser} className="w-3 h-3 mr-1" />
                          {displayName}
                        </span>
                      );
                    })}
                    {result.availableMembers && result.availableMembers.length > 5 && (
                      <span className="inline-block bg-accent/10 text-accent px-3 py-1 rounded-xl text-sm font-semibold">
                        +{result.availableMembers.length - 5} more
                      </span>
                    )}
                  </div>
                  </div>
                )}
              </div>{/* End flex wrapper */}

              {/* Action Buttons */}
              <div className="mt-6 pt-4 border-t border-border-subtle flex flex-col md:flex-row md:justify-end gap-2">
                {/* View on GolfNow button (if GolfNow result) */}
                {hasGolfNow && golfnowBookingUrl && (
                  <a
                    href={golfnowBookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="w-full md:w-auto px-5 py-2.5 rounded-lg border border-border-subtle text-text-secondary hover:bg-surface transition-colors text-sm font-medium flex items-center justify-center"
                  >
                    <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5 mr-2" />
                    View on GolfNow
                  </a>
                )}

                {/* Create Round button - always primary */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateRound(result);
                  }}
                  disabled={isCreatingRound}
                  className={`w-full md:w-auto px-5 py-2.5 rounded-lg bg-accent text-white hover:bg-interactive-hover transition-colors text-sm font-medium flex items-center justify-center ${isCreatingRound ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <SafeIcon icon={isCreatingRound ? FiClock : FiCheck} className="w-3.5 h-3.5 mr-2" />
                  {isCreatingRound ? 'Creating...' : isSolo ? 'Create Round' : 'Create Round & Send Invites'}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default SmartResultCard;